"use client";
import React from "react";
import { MessageSquare, Star } from "lucide-react";
import ReviewCard, { ReviewProps } from "components/common/Card/Review";
import Pagination from "components/common/Pagination";
import ReviewService from "services/review";
import { get } from "utils/lodash";
import usePagination from "hooks/usePagination";

interface ReviewSectionProps {
  shopId: string;
  data?: unknown[];
  totalReviews?: number;
  averageRating?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
}

const buildQuery = (payload: {
  shopId: string;
  page: number;
  limit: number;
}) => {
  return {
    where: {
      shop: {
        equals: payload.shopId,
      },
      status: {
        equals: "approved",
      },
    },
    sort: "-createdAt",
    fetchPolicy: "no-cache",
    page: get(payload, "page", 1),
    limit: get(payload, "limit", 12),
  };
};
const ReviewSection = ({
  shopId,
  averageRating = 0,
  onLoadMore,
  hasMore = false,
  loading = false,
}: ReviewSectionProps) => {
  const pagination = usePagination({
    initialParams: {
      page: 1,
      limit: 10,
    },
    loader: async (payload) => {
      const query = buildQuery({
        shopId,
        page: get(payload, "page", 1),
        limit: get(payload, "limit", 10),
      });
      const res = await ReviewService.find(query);
      const data = get(res, "data", []);
      const total = get(res, "params.total", 0);
      return {
        data,
        total,
      };
    },
  });

  React.useEffect(() => {
    pagination?.onReload?.();
  }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalReviews = get(pagination, "total", 0);
  console.log({ reviews: pagination?.items });
  return (
    <div className="container mx-auto p-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Đánh giá</h1>
        </div>

        <div className="flex items-center gap-4">
          <p className="text-gray-600">{totalReviews} đánh giá</p>
          {averageRating > 0 && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-gray-700 font-medium">
                {averageRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Reviews Grid */}
      {pagination?.items?.length > 0 ? (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pagination?.items?.map((review: unknown, index: number) => (
              <ReviewCard
                key={get(review, "id", index)}
                {...(review as ReviewProps)}
                className="w-full"
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={onLoadMore}
                disabled={loading}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? "Đang tải..." : "Xem thêm đánh giá"}
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Chưa có đánh giá nào
          </h3>
          <p className="text-gray-500">Cửa hàng này chưa có đánh giá nào.</p>
        </div>
      )}
      <Pagination pagination={pagination} />
    </div>
  );
};

export default ReviewSection;
